import { useEffect, useState } from "react"
import { fastSimonToShopifyProduct } from "../utils/dataProvider";
import { multilingual_url } from '../utils/multilingual-url';
import { themeI18n } from "../utils/i18n_helper";
import { Price } from "./Price";

import Icons from './Icons';

const noResultsTemplate = document.querySelector('[data-no-results-template]').innerHTML;

export const PredictiveSearchFS = ({ root }) => {
  const searchInput = root.closest('.search-input-parent').querySelector('[data-search-input]');
  const clearButton = root.closest('.search-input-parent').querySelector('[data-predictive-search-clear-btn]');
  const [searchResults, setSearchResults] = useState();
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const onSearchInputChange = async (event) => {
    setSearchQuery(event.target.value);
    setIsLoading(true);

    window.FastSimonSDK.instantSearch({
      query: event.target.value,
      withAttributes: true,
      withProductVariants: true,
      callback: async (response) => {
        setSearchResults(response);
        setIsLoading(false);
      }
    });

    // Might need to fetch Pages and articles from Shopify
    // const shopifyPredictiveSearch = await fetch(multilingual_url('/search/suggest.json', { query: event.target.value }));
  }

  const clearSearch = () => {
    setSearchQuery('');
    searchInput.value = '';
  }

  useEffect(() => {
    searchInput.addEventListener('input', onSearchInputChange);

    if (clearButton) {
      clearButton.addEventListener('click', clearSearch);
    }

    return () => {
      searchInput.removeEventListener('input', onSearchInputChange);

      if (clearButton) {
        clearButton.addEventListener('click', clearSearch);
      }
    }
  }, []);

  useEffect(() => {
    if (clearButton) {
      clearButton.classList.toggle('hidden', !searchQuery);
    }
  }, [searchQuery]);

  const hasSearchQuery = searchQuery !== '';
  const hasPopularSearches = searchResults?.payload?.popularSearches?.length > 0;
  const hasProducts = searchResults?.payload?.products?.length > 0;
  const hasCollections = searchResults?.payload?.categories?.length > 0;
  const hasResults = hasProducts || hasCollections;

  return (
    <>
      {/* Popular Searches */}
      {hasPopularSearches && (
        <div className="predictive-search__popular-searches">
          {searchResults?.payload.popularSearches.map((popularSearch, index) => <PredictiveSearchPopularSearch key={index} popularSearch={popularSearch} searchQuery={searchQuery} searchInput={searchInput} />)}
        </div>
      )}

      {/* Search Results */}
      {(hasSearchQuery && hasResults) && (
        <div className="predictive-search__results-content">
          {/* Product results */}
          {(hasProducts) && (
            <div className="predictive-search__column predictive-search__column--products">
              <div className="predictive-search__column-title-wrapper hide-desktop">
                <p className="predictive-search__column-title">{themeI18n('search.products')}</p>

                <a href={multilingual_url(`/search?q=${searchQuery}`)} className="predictive-search__view-all">{themeI18n('search.all_products')}</a>
              </div>

              <div className="predictive-search__products-grid">
                {searchResults?.payload.products.map((product) => <PredictiveSearchProduct key={product.id} product={product} searchQuery={searchQuery} />)}
              </div>
            </div>
          )}

          {/* Collections */}
          {(hasCollections) && (
            <div className="predictive-search__column predictive-search__column--collections">
              <div className="predictive-search__column-title-wrapper">
                <p className="predictive-search__column-title">{themeI18n('search.collections')}</p>
              </div>

              <div className="predictive-search__results-list">
                {searchResults?.payload.categories.map((collection) => <PredictiveSearchCollection key={collection.id} collection={collection} searchQuery={searchQuery} />)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Search no results */}
      {(hasSearchQuery && !hasResults && !isLoading) &&
        <>
          <p className="predictive-search__no-results-title">{themeI18n('search.no_results', {terms: searchQuery})}</p>

          {noResultsTemplate && (
            <div dangerouslySetInnerHTML={{ __html: noResultsTemplate }} />
          )}
        </>
      }
    </>
  )
}

const PredictiveSearchProduct = ({ product, searchQuery }) => {
  const shopifyProduct = fastSimonToShopifyProduct(product);
  // const allowAddToCart = shopifyProduct.metafields.addtocart.active === true;

  const onHrefClick = (event) => {
    event.preventDefault();

    // Report Product Click to FS
    window.FastSimonSDK.event({
      eventName: window.FastSimonEventName.AutocompleteProductClicked,
      data: {
        query: searchQuery,
        productID: shopifyProduct.id,
        productQuery: product.s, // product's 's' key (Required)
      }
    });

    window.location = multilingual_url(shopifyProduct.url);
  }

  // Only render if shopifyProduct.featured_image is not blank
  if (!shopifyProduct.featured_image) return null;

  return (
    <div className="predictive-search__product-card">
      <a href={multilingual_url(shopifyProduct.url)} onClick={(event) => onHrefClick(event)} className="predictive-search__product-image">
        <img src={shopifyProduct.featured_image} alt={shopifyProduct.title} />
      </a>
      <div className="predictive-search__product-content text-small">
        <a href={multilingual_url(shopifyProduct.url)} onClick={(event) => onHrefClick(event)} className="predictive-search__product-title single-line-text" title={shopifyProduct.title}>
          {shopifyProduct.title}
        </a>
        <Price product={shopifyProduct} />
      </div>
    </div>
  )
}

const PredictiveSearchCollection = ({ collection, searchQuery }) => {
  const onHrefClick = (event) => {
    event.preventDefault();

    window.FastSimonSDK.event({
      eventName: window.FastSimonEventName.AutocompleteCategoryClicked,
      data: {
        query: searchQuery,
        collectionID: collection.id
      }
    });

    window.location = multilingual_url(collection.u);
  }

  return (
    <a onClick={(event) => onHrefClick(event)} href={multilingual_url(collection.u)} className="predictive-search__list-item single-line-text" title={collection.l}>
      {collection.l}
    </a>
  )
}

const PredictiveSearchPopularSearch = ({ popularSearch, searchQuery, searchInput }) => {
  const onClick = () => {
    searchInput.value = popularSearch.label;
    searchInput.dispatchEvent(new Event('input')); // manually trigger input event
    searchInput.focus();

    // Report Popular Search Click to FS
    window.FastSimonSDK.event({
      eventName: window.FastSimonEventName.AutocompletePopularClicked,
      data: {
        query: searchQuery,
        term: popularSearch.label
      }
  });
  }

  return (
    <button type="button" className="suggestion-item" onClick={() => onClick()}>
      <Icons icon='search' size='small' />

      <p className="suggestion-item__text text-small">
        {popularSearch.label}
      </p>
    </button>
  )
}
